import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { FiMail, FiLock, FiUser } from 'react-icons/fi';
import { authAPI } from '../services/api';
import NaatiLogo from './NaatiLogo';
import './SignUp.css';

interface SignUpProps {
  onSignUp: (
    account: string,
    password: string,
    passwordConfirm: string,
    organization?: string,
    firstName?: string,
    lastName?: string,
    mode?: 'email' | 'username'
  ) => Promise<void>;
  onBackToLogin?: () => void;
}

const USERNAME_PATTERN = /^[\w.@+-]+$/u;

const SignUp: React.FC<SignUpProps> = ({ onSignUp, onBackToLogin }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    account: '',
    password: '',
    passwordConfirm: '',
    organization: '',
    firstName: '',
    lastName: ''
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isLoading, setIsLoading] = useState(false);
  const [allowUsernameSignup, setAllowUsernameSignup] = useState(false);
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const s = await authAPI.getPublicPlatformSettings();
        if (!cancelled) setAllowUsernameSignup(Boolean(s.allow_username_signup));
      } catch {
        if (!cancelled) setAllowUsernameSignup(false);
      } finally {
        if (!cancelled) setSettingsLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};
    const acc = formData.account.trim();

    if (!acc) {
      newErrors.account = allowUsernameSignup ? 'Username is required' : 'Email is required';
    } else if (allowUsernameSignup) {
      if (acc.length < 3) {
        newErrors.account = 'Username must be at least 3 characters';
      } else if (acc.length > 150) {
        newErrors.account = 'Username is too long';
      } else if (!USERNAME_PATTERN.test(acc)) {
        newErrors.account = 'Use letters, numbers, or _ . @ + - only';
      }
    } else if (!/\S+@\S+\.\S+/.test(acc)) {
      newErrors.account = 'Email is invalid';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    if (!formData.passwordConfirm) {
      newErrors.passwordConfirm = 'Please confirm your password';
    } else if (formData.password !== formData.passwordConfirm) {
      newErrors.passwordConfirm = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    try {
      await onSignUp(
        formData.account.trim(),
        formData.password,
        formData.passwordConfirm,
        formData.organization || undefined,
        formData.firstName || undefined,
        formData.lastName || undefined,
        allowUsernameSignup ? 'username' : 'email'
      );
    } catch (error: any) {
      console.error('Sign up error:', error);
      setErrors({ submit: error.message || 'Registration failed. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.5,
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.4, 0, 0.2, 1] as const
      }
    }
  };

  const logoVariants = {
    hidden: { scale: 0, opacity: 0 },
    visible: {
      scale: 1,
      opacity: 1,
      transition: {
        type: 'spring' as const,
        stiffness: 200,
        damping: 15
      }
    }
  };

  return (
    <div className="signup-container">
      <motion.div
        className="signup-background"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        <div className="gradient-orb orb-1"></div>
        <div className="gradient-orb orb-2"></div>
        <div className="gradient-orb orb-3"></div>
      </motion.div>

      <motion.div
        className="signup-card"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <motion.div
          className="signup-logo"
          variants={logoVariants}
        >
          <NaatiLogo size="lg" />
        </motion.div>

        <motion.h1
          className="signup-title"
          variants={itemVariants}
        >
          Create Account
        </motion.h1>

        <motion.p
          className="signup-subtitle"
          variants={itemVariants}
        >
          Sign up to get started with NAATI Assessment Platform
        </motion.p>

        <motion.form
          className="signup-form"
          onSubmit={handleSubmit}
          variants={itemVariants}
        >
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">
                {React.createElement(FiUser as React.ComponentType<{ size?: number }>, { size: 18 })}
                First Name
              </label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                className="form-input"
                placeholder="Enter your first name"
              />
            </div>

            <div className="form-group">
              <label className="form-label">
                {React.createElement(FiUser as React.ComponentType<{ size?: number }>, { size: 18 })}
                Last Name
              </label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                className="form-input"
                placeholder="Enter your last name"
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">
              {allowUsernameSignup
                ? React.createElement(FiUser as React.ComponentType<{ size?: number }>, { size: 18 })
                : React.createElement(FiMail as React.ComponentType<{ size?: number }>, { size: 18 })}
              {allowUsernameSignup ? 'Username' : 'Email Address'}
            </label>
            <input
              type={allowUsernameSignup ? 'text' : 'email'}
              name="account"
              value={formData.account}
              onChange={handleChange}
              className={`form-input ${errors.account ? 'error' : ''}`}
              placeholder={allowUsernameSignup ? 'Choose a username' : 'Enter your email'}
              autoComplete={allowUsernameSignup ? 'username' : 'email'}
              disabled={!settingsLoaded}
              required
            />
            {errors.account && <span className="error-message">{errors.account}</span>}
          </div>

          <div className="form-group">
            <label className="form-label">
              Organization (Optional)
            </label>
            <input
              type="text"
              name="organization"
              value={formData.organization}
              onChange={handleChange}
              className="form-input"
              placeholder="Enter your organization"
            />
          </div>

          <div className="form-group">
            <label className="form-label">
              {React.createElement(FiLock as React.ComponentType<{ size?: number }>, { size: 18 })}
              Password
            </label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              className={`form-input ${errors.password ? 'error' : ''}`}
              placeholder="Create a password"
              required
            />
            {errors.password && <span className="error-message">{errors.password}</span>}
          </div>

          <div className="form-group">
            <label className="form-label">
              {React.createElement(FiLock as React.ComponentType<{ size?: number }>, { size: 18 })}
              Confirm Password
            </label>
            <input
              type="password"
              name="passwordConfirm"
              value={formData.passwordConfirm}
              onChange={handleChange}
              className={`form-input ${errors.passwordConfirm ? 'error' : ''}`}
              placeholder="Confirm your password"
              required
            />
            {errors.passwordConfirm && <span className="error-message">{errors.passwordConfirm}</span>}
          </div>

          {errors.submit && (
            <div className="error-message submit-error">
              {errors.submit}
            </div>
          )}

          <motion.button
            type="submit"
            className="signup-button"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            disabled={isLoading || !settingsLoaded}
          >
            {isLoading ? 'Creating Account...' : 'Create Account'}
          </motion.button>
        </motion.form>

        <motion.div
          className="signup-footer"
          variants={itemVariants}
        >
          <p className="footer-text">
            Already have an account?{' '}
            <button
              type="button"
              className="link-button"
              onClick={() => {
                if (onBackToLogin) {
                  onBackToLogin();
                }
                navigate('/login');
              }}
            >
              Sign In
            </button>
          </p>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default SignUp;

